import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  KeyRound, Webhook, Copy, Check, ArrowRight, ShieldCheck,
  Gauge, PackagePlus, MapPin, XCircle, Tag, AlertTriangle, MapPinned, Code2,
  Globe2, Wallet, Truck, RotateCcw, FlaskConical, Activity, Repeat,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, GradientCta, fadeUp } from "../components/marketing/Blocks";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const CAPABILITIES = [
  { icon: Gauge, title: "Rates API", desc: "Live multi-courier rates with actual, volumetric and chargeable weight." },
  { icon: MapPinned, title: "Serviceability API", desc: "Pincode serviceability, pickup/delivery availability, COD and TAT." },
  { icon: PackagePlus, title: "Shipment API", desc: "Single and bulk booking with courier selection and idempotency." },
  { icon: MapPin, title: "Tracking API", desc: "Normalized tracking timelines by shipment ID or AWB." },
  { icon: Tag, title: "Label API", desc: "PNG/PDF shipping labels with per-box support for heavy shipments." },
  { icon: Truck, title: "Pickups & Manifests", desc: "Schedule pickups and generate manifests for handover." },
  { icon: AlertTriangle, title: "NDR & RTO API", desc: "Act on delivery exceptions — reattempt, reschedule or return." },
  { icon: RotateCcw, title: "Returns API", desc: "Create reverse shipments, exchanges and replacements." },
  { icon: Globe2, title: "International API", desc: "Cross-border rates, booking, customs data, labels and invoices." },
  { icon: Wallet, title: "Wallet API", desc: "Balance, credit limit and full transaction ledger." },
  { icon: Webhook, title: "Webhooks", desc: "HMAC-signed lifecycle events with retries and replay." },
  { icon: XCircle, title: "Cancellation API", desc: "Cancel before pickup with automatic wallet refund." },
];

const ENDPOINT_GROUPS = [
  { group: "Rates & Serviceability", rows: [
    ["POST", "/v1/domestic/serviceability", "Pincode serviceability, pickup/delivery availability, COD and TAT."],
    ["POST", "/v1/domestic/rates", "Live multi-courier rates for a lane, weight, dimensions and payment mode."],
    ["POST", "/v1/international/serviceability", "Destination-country serviceability check."],
    ["POST", "/v1/international/rates", "International multi-courier rates."],
  ]},
  { group: "Shipments", rows: [
    ["POST", "/v1/domestic/shipments", "Create a shipment — courier selection, AWB and wallet debit in one call."],
    ["POST", "/v1/domestic/shipments/bulk", "Create multiple shipments in one request."],
    ["GET", "/v1/shipments", "List your shipments with status filters."],
    ["GET", "/v1/shipments/{shipment_id}", "Fetch one shipment with full detail."],
    ["POST", "/v1/shipments/{shipment_id}/cancel", "Cancel before pickup with automatic wallet refund."],
    ["POST", "/v1/international/shipments", "Create an international shipment with item-level customs data."],
    ["GET", "/v1/international/shipments/{shipment_id}", "Fetch an international shipment."],
  ]},
  { group: "Drafts, Presets & Receivers", rows: [
    ["POST", "/v1/drafts", "Save or update a shipment draft — book it later with courier selection."],
    ["GET", "/v1/drafts", "List your saved shipment drafts."],
    ["GET", "/v1/drafts/{draft_id}", "Fetch one draft with its full payload."],
    ["DELETE", "/v1/drafts/{draft_id}", "Delete a draft."],
    ["GET", "/v1/package-presets", "List saved package dimension presets."],
    ["POST", "/v1/package-presets", "Create a package preset (name, weight, L×W×H)."],
    ["DELETE", "/v1/package-presets/{preset_id}", "Delete a package preset."],
    ["GET", "/v1/receivers/search", "Search repeat receivers by name or phone for autofill."],
  ]},
  { group: "Labels & Documents", rows: [
    ["POST", "/v1/shipments/{shipment_id}/label", "Generate a PNG/PDF label (per-box for heavy shipments)."],
    ["GET", "/v1/shipments/{shipment_id}/label/download", "Download the generated label file."],
    ["GET", "/v1/international/shipments/{shipment_id}/label", "International shipping label."],
    ["GET", "/v1/international/shipments/{shipment_id}/invoice", "International customs invoice."],
  ]},
  { group: "Tracking", rows: [
    ["GET", "/v1/shipments/{shipment_id}/tracking", "Normalized tracking timeline for one shipment."],
    ["GET", "/v1/tracking/{awb}", "Track directly by AWB number."],
    ["GET", "/v1/international/shipments/{shipment_id}/tracking", "International tracking timeline."],
  ]},
  { group: "Pickups & Manifests", rows: [
    ["POST", "/v1/pickups", "Schedule a pickup for one or more shipments."],
    ["GET", "/v1/pickups/{pickup_id}", "Pickup status and details."],
    ["POST", "/v1/pickups/{pickup_id}/cancel", "Cancel a scheduled pickup."],
    ["POST", "/v1/manifests", "Generate a shipment manifest."],
    ["GET", "/v1/manifests/{manifest_id}", "Fetch or download a manifest."],
  ]},
  { group: "NDR, RTO & Returns", rows: [
    ["GET", "/v1/ndr", "List non-delivery reports."],
    ["POST", "/v1/ndr/{shipment_id}/action", "Reattempt, reschedule, update address/phone, hold or request RTO."],
    ["GET", "/v1/rto", "List RTO shipments and details."],
    ["POST", "/v1/returns", "Create a reverse shipment, exchange or replacement."],
    ["GET", "/v1/returns/{return_id}", "Return shipment status."],
  ]},
  { group: "Pickup Addresses", rows: [
    ["POST", "/v1/pickup-addresses", "Save a pickup address and pre-register it with courier partners (production keys)."],
    ["GET", "/v1/pickup-addresses", "List saved pickup addresses with courier registration status."],
  ]},
  { group: "Wallet", rows: [
    ["GET", "/v1/wallet/balance", "API wallet balance, credit limit and available credit."],
    ["GET", "/v1/wallet/transactions", "Full transaction ledger with debit/credit entries."],
  ]},
];

const EVENTS = [
  "shipment.created", "shipment.booked", "awb.assigned", "pickup.scheduled", "picked_up",
  "in_transit", "out_for_delivery", "delivered", "ndr", "ndr_resolved", "rto_initiated",
  "rto_in_transit", "rto_delivered", "cancelled", "lost", "damaged", "return_created",
  "return_picked", "return_delivered", "wallet.debited", "wallet.refunded", "test.webhook",
];

const RATE_BODY = `{
  "from_pincode": "110001",
  "to_pincode": "400001",
  "weight": 1.5,
  "length": 30, "breadth": 20, "height": 10,
  "payment_mode": "prepaid"
}`;

const SHIPMENT_BODY = `{
  "order_id": "ORD-10042",
  "customer_name": "Aarav Shah",
  "phone": "9876543210",
  "address": "221, MG Road",
  "city": "Mumbai", "state": "Maharashtra", "pincode": "400001",
  "items": [{ "name": "Cotton T-Shirt", "qty": 2, "price": 499, "weight": 0.75 }],
  "weight": 1.5,
  "length": 30, "breadth": 20, "height": 10,
  "invoice_value": 998,
  "invoice_number": "INV-10042",
  "eway_bill": "123456789012",
  "receiver_gstin": "27ABCDE1234F1Z5",
  "customer_reference": "MY-REF-0042",
  "payment_mode": "prepaid",
  "pickup_address": {
    "name": "My Warehouse", "phone": "9876543210",
    "address": "Plot 12, Industrial Area",
    "city": "Delhi", "state": "Delhi", "pincode": "110001"
  }
}`;

const SAMPLES = {
  "cURL": `curl -X POST ${BASE_URL}/domestic/rates \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: shipany_test_sk_YOUR_KEY" \\
  -d '${RATE_BODY.replace(/\n/g, "").replace(/ +/g, " ")}'`,
  "Node.js": `const resp = await fetch("${BASE_URL}/domestic/rates", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": "shipany_test_sk_YOUR_KEY",
  },
  body: JSON.stringify(${RATE_BODY.replace(/\n/g, " ")}),
});
const { success, data, request_id } = await resp.json();
console.log(data.quotes);`,
  "PHP": `$ch = curl_init("${BASE_URL}/domestic/rates");
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => [
    "Content-Type: application/json",
    "X-API-Key: shipany_test_sk_YOUR_KEY",
  ],
  CURLOPT_POSTFIELDS => json_encode($payload),
]);
$result = json_decode(curl_exec($ch), true);
print_r($result["data"]["quotes"]);`,
  "Python": `import requests

resp = requests.post(
    "${BASE_URL}/domestic/rates",
    headers={
        "Content-Type": "application/json",
        "X-API-Key": "shipany_test_sk_YOUR_KEY",
    },
    json=${RATE_BODY.replace(/"/g, "'")},
)
result = resp.json()
print(result["success"], result["request_id"])`,
};

const SIDEBAR = [
  { group: "Introduction", items: [["Overview", "overview"], ["Quick Start", "quickstart"]] },
  { group: "Authentication", items: [["API Keys", "authentication"], ["Sandbox & Live", "authentication"], ["Envelopes & Limits", "authentication"]] },
  { group: "Shipping", items: [["Get Rates", "shipping"], ["Create Shipment", "shipping"], ["Drafts & Presets", "reference-endpoints"], ["Endpoint Reference", "reference-endpoints"]] },
  { group: "Tracking", items: [["Track Shipment", "tracking"], ["Webhook Events", "webhooks"]] },
  { group: "Operations", items: [["Labels & Manifests", "labels"], ["NDR & RTO", "ndr"], ["Returns", "ndr"]] },
  { group: "International", items: [["Intl Rates & Booking", "international"], ["Customs Documents", "international"]] },
  { group: "Wallet", items: [["Balance & Ledger", "wallet"]] },
  { group: "Reference", items: [["Errors", "reference"], ["Status Page", "reference"], ["Changelog", "reference"]] },
];

const ERRORS = [
  ["400", "Bad request — invalid input such as a malformed pincode or out-of-range weight/dimension."],
  ["401", "INVALID_API_KEY — missing, invalid, expired or revoked X-API-Key."],
  ["402", "Insufficient API wallet balance to create the shipment."],
  ["403", "FORBIDDEN — account suspended, sandbox disabled, or key lacks the required permission."],
  ["404", "Resource not found — e.g. a shipment that does not belong to your API account."],
  ["422", "Validation failed — mandatory shipment fields are missing or invalid."],
  ["429", "Rate limit exceeded for your API key (default 60 requests/minute)."],
  ["503", "COURIER_UNAVAILABLE — no courier could serve the request; no charge is made."],
];

function CodeConsole() {
  const [tab, setTab] = useState("cURL");
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = SAMPLES[tab];
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { toast.error("Copy failed — select the text manually"); }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#030B18]" data-testid="dev-code-console">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <div className="flex gap-1">
          {Object.keys(SAMPLES).map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`dev-code-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-lg px-3 py-1.5 text-[11px] font-bold transition-colors ${tab === t ? "bg-shipany-cyan/15 text-shipany-cyan" : "text-slate-400 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>
        <button onClick={copy} data-testid="dev-code-copy-btn"
          className="flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1 text-[11px] font-semibold text-slate-300 transition-colors hover:bg-white/5">
          {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="border-b border-white/10 px-4 py-2">
        <span className="mono text-[11px] font-bold text-[#FF9933]">POST</span>
        <span className="mono ml-2 text-[11px] text-slate-300">/api/v1/domestic/rates</span>
        <span className="mono ml-3 text-[10px] text-slate-500">X-API-Key: shipany_test_sk_… · Content-Type: application/json</span>
      </div>
      <pre className="mono max-h-80 overflow-auto p-4 text-xs leading-relaxed text-slate-300" data-testid="dev-code-sample">{SAMPLES[tab]}</pre>
    </div>
  );
}

function EndpointRow({ method, path, desc }) {
  return (
    <div data-testid={`dev-endpoint-${path.split("/").pop().replace(/[{}]/g, "") || "root"}`}
      className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
      <span className={`mono rounded px-1.5 py-0.5 text-[10px] font-bold ${method === "GET" ? "bg-emerald-500/15 text-emerald-400" : "bg-[#FF9933]/15 text-[#FF9933]"}`}>{method}</span>
      <span className="mono break-all text-xs font-bold text-white">{path}</span>
      <span className="w-full text-xs text-slate-400 sm:w-auto sm:flex-1">{desc}</span>
    </div>
  );
}

function DocSection({ id, title, kicker, children, testId }) {
  return (
    <motion.section id={id} {...fadeUp} className="scroll-mt-24" data-testid={testId || `dev-section-${id}`}>
      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-shipany-cyan">{kicker}</span>
      <h2 className="font-display mt-2 text-xl font-bold text-white sm:text-2xl">{title}</h2>
      <div className="mt-5">{children}</div>
    </motion.section>
  );
}

export default function Developers() {
  return (
    <MarketingLayout>
      <div data-testid="developers-page">
        <MarketingHero kicker="ShipAny API v1" title="One API. Every Courier."
          sub="Shipping infrastructure for ecommerce platforms, marketplaces, ERPs and SaaS products — domestic and international rates, booking, labels, tracking, NDR, returns, wallet and webhooks.">
          <motion.div {...fadeUp} className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link to="/console/login" data-testid="dev-get-access-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Open API Console <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="/downloads/shipany-api-sandbox-kit.pdf" download data-testid="dev-sandbox-kit-download"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              <Download className="h-4 w-4" /> Download Sandbox Kit
            </a>
            <a href="#reference-endpoints" data-testid="dev-reference-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
              View API Reference <Code2 className="h-4 w-4" />
            </a>
          </motion.div>
        </MarketingHero>

        <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="dev-quickstart-band">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Quick start" title="Live in Three Steps" />
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                ["01", "Get API Access", "Contact ShipAny to enable your API account, then sign in to the API Console — separate from the merchant dashboard."],
                ["02", "Create a Sandbox Key", "Generate a shipany_test_sk_… secret key in Console → API Keys. Keys are shown once — store them securely."],
                ["03", "Start Integrating", "Call sandbox endpoints with simulated couriers and labels — no live bookings, no wallet charges. Rotate to a live key when ready."],
              ].map(([n, t, d], i) => (
                <motion.div key={n} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`dev-step-${i + 1}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                  <span className="mono text-xs font-bold text-[#FF9933]">{n}</span>
                  <h3 className="font-display mt-2 text-base font-bold text-white">{t}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-16 sm:py-20" data-testid="dev-docs-section">
          <div className="mx-auto max-w-7xl px-5">
            <div className="mb-8 flex flex-wrap items-center gap-3">
              <a href="/downloads/shipany-api-documentation.pdf" download data-testid="dev-api-docs-download"
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-6 py-3 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
                <Download className="h-4 w-4" /> Download API Docs (PDF)
              </a>
              <a href="/downloads/shipany-api-sandbox-kit.pdf" download data-testid="dev-sandbox-kit-download-2"
                className="flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
                <Download className="h-4 w-4" /> Sandbox Kit (PDF)
              </a>
            </div>
            <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
              <aside className="hidden lg:block">
                <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.04] p-5" data-testid="dev-docs-sidebar">
                  {SIDEBAR.map((g) => (
                    <div key={g.group} className="mt-4 first:mt-0">
                      <p className="font-display text-[10px] font-bold uppercase tracking-widest text-slate-500">{g.group}</p>
                      <nav className="mt-1.5 space-y-0.5">
                        {g.items.map(([label, id]) => (
                          <a key={label} href={`#${id}`} data-testid={`dev-nav-${g.group.toLowerCase().replace(/[^a-z]+/g, "-")}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="block rounded-lg px-3 py-1.5 text-xs font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-shipany-cyan">
                            {label}
                          </a>
                        ))}
                      </nav>
                    </div>
                  ))}
                </div>
              </aside>

              <div className="space-y-16">
                <DocSection id="overview" kicker="Introduction" title="API Capabilities">
                  <p className="text-sm leading-relaxed text-slate-400">
                    The ShipAny v1 REST API is authenticated with API keys and scoped to your API account and isolated wallet.
                    Base URL: <span className="mono rounded bg-white/5 px-2 py-0.5 text-xs text-shipany-cyan">{BASE_URL}</span>
                  </p>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {CAPABILITIES.map((c) => (
                      <div key={c.title} data-testid={`dev-capability-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20">
                          <c.icon className="h-5 w-5 text-shipany-cyan" />
                        </span>
                        <div>
                          <h3 className="font-display text-sm font-bold text-white">{c.title}</h3>
                          <p className="mt-1 text-xs leading-relaxed text-slate-400">{c.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </DocSection>

                <DocSection id="authentication" kicker="Authentication" title="API Keys, Sandbox & Envelopes">
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                      <h3 className="flex items-center gap-2 font-display text-sm font-bold text-white"><KeyRound className="h-4 w-4 text-shipany-cyan" /> X-API-Key header</h3>
                      <p className="mt-2 text-xs leading-relaxed text-slate-400">
                        Generate sandbox (<span className="mono text-slate-200">shipany_test_sk_…</span>) or live (<span className="mono text-slate-200">shipany_live_sk_…</span>) secret keys in the API Console. Keys are stored hashed, shown once at creation, and can be rotated or revoked instantly. Send the key with every request:
                      </p>
                      <div className="mono mt-3 rounded-xl bg-[#030B18] p-4 text-xs text-slate-300">X-API-Key: shipany_test_sk_…</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid="dev-sandbox-card">
                      <h3 className="flex items-center gap-2 font-display text-sm font-bold text-white"><FlaskConical className="h-4 w-4 text-[#FF9933]" /> Sandbox mode</h3>
                      <p className="mt-2 text-xs leading-relaxed text-slate-400">
                        Sandbox keys return simulated couriers (<span className="mono text-slate-200">sandbox_express</span>, <span className="mono text-slate-200">sandbox_surface</span>, <span className="mono text-slate-200">sandbox_heavy</span>), simulated <span className="mono text-slate-200">SBX…</span> AWBs and real PDF labels — with zero live courier calls and zero wallet movement. Integrate and test end-to-end, then switch to a live key for production traffic.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                      <h3 className="flex items-center gap-2 font-display text-sm font-bold text-white"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Envelopes, request IDs & limits</h3>
                      <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-slate-400">
                        <li>· Every response uses a standard envelope: <span className="mono text-slate-200">{"{success, data, request_id}"}</span> or <span className="mono text-slate-200">{"{success:false, error:{code,message,request_id}}"}</span>.</li>
                        <li>· Every request carries an <span className="mono text-slate-200">X-Request-ID</span> header — include it when contacting support.</li>
                        <li>· Bookings accept an <span className="mono text-slate-200">Idempotency-Key</span> header — safe retries return the same shipment without duplicates.</li>
                        <li>· Default rate limit is 60 requests/minute per key; usage is reported via <span className="mono text-slate-200">X-RateLimit-Limit / Remaining / Reset</span> headers.</li>
                        <li>· Courier credentials never leave ShipAny servers — your application only talks to the ShipAny API.</li>
                      </ul>
                    </div>
                  </div>
                </DocSection>

                <DocSection id="shipping" kicker="Shipping" title="Get Rates & Create Shipments">
                  <p className="text-sm leading-relaxed text-slate-400">
                    Rates are quoted live from couriers that actually service the lane and weight — an empty quote list means no courier currently serves it. Bookings debit your API wallet atomically and refund automatically on cancellation before pickup.
                  </p>
                  <div className="mt-5">
                    <CodeConsole />
                  </div>
                  <div className="mt-5 rounded-2xl border border-white/10 bg-[#030B18] p-5">
                    <p className="mono text-[11px] font-bold text-slate-400">POST /v1/domestic/shipments — request body</p>
                    <pre className="mono mt-3 max-h-72 overflow-auto text-xs leading-relaxed text-slate-300" data-testid="dev-shipment-body">{SHIPMENT_BODY}</pre>
                  </div>
                </DocSection>

                <DocSection id="reference-endpoints" kicker="Reference" title="Endpoint Reference">
                  <div className="space-y-6">
                    {ENDPOINT_GROUPS.map((g) => (
                      <div key={g.group}>
                        <h3 className="font-display text-sm font-bold text-white">{g.group}</h3>
                        <div className="mt-3 space-y-2">
                          {g.rows.map(([m, p, d]) => <EndpointRow key={`${m}-${p}`} method={m} path={p} desc={d} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                </DocSection>

                <DocSection id="tracking" kicker="Tracking" title="Track Shipments">
                  <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
                    <span className="mono rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400">GET</span>
                    <span className="mono ml-2 text-xs font-bold text-white">/v1/tracking/{"{awb}"}</span>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400">
                      Returns the full shipment record with a normalized tracking timeline — every courier scan mapped to unified ShipAny statuses, scoped to shipments owned by your API account. For push updates, use webhooks instead of polling.
                    </p>
                  </div>
                </DocSection>

                <DocSection id="labels" kicker="Operations" title="Labels, Pickups & Manifests">
                  <p className="text-sm leading-relaxed text-slate-400">
                    Labels are generated on demand as PNG or PDF — for heavy multi-box shipments each physical box gets its own label (per-dimension limit 306 cm per box). Schedule pickups for one or more shipments and generate manifests for warehouse handover via the pickups and manifests endpoints.
                  </p>
                </DocSection>

                <DocSection id="ndr" kicker="Operations" title="NDR, RTO & Returns">
                  <p className="text-sm leading-relaxed text-slate-400">
                    Non-Delivery Reports are listed via <span className="mono rounded bg-white/5 px-1.5 py-0.5 text-xs text-slate-200">GET /v1/ndr</span> with the failure reason. Act on them with <span className="mono rounded bg-white/5 px-1.5 py-0.5 text-xs text-slate-200">POST /v1/ndr/{"{shipment_id}"}/action</span> — reattempt, reschedule, update address/phone, hold or request RTO. Reverse shipments, exchanges and replacements are created via <span className="mono rounded bg-white/5 px-1.5 py-0.5 text-xs text-slate-200">POST /v1/returns</span>. Subscribe to <span className="mono rounded bg-white/5 px-1.5 py-0.5 text-xs text-slate-200">ndr</span>, <span className="mono rounded bg-white/5 px-1.5 py-0.5 text-xs text-slate-200">rto_initiated</span> and <span className="mono rounded bg-white/5 px-1.5 py-0.5 text-xs text-slate-200">return_created</span> webhook events to react in real time.
                  </p>
                </DocSection>

                <DocSection id="webhooks" kicker="Webhooks" title="Real-Time Shipment Events">
                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid="dev-webhook-events-card">
                      <h3 className="flex items-center gap-2 font-display text-sm font-bold text-white"><Webhook className="h-4 w-4 text-shipany-cyan" /> Event types</h3>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {EVENTS.map((ev) => (
                          <span key={ev} data-testid={`dev-event-${ev.replace(/\./g, "-")}`}
                            className="mono rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-300">{ev}</span>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid="dev-webhook-security-card">
                      <h3 className="flex items-center gap-2 font-display text-sm font-bold text-white"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Signature verification</h3>
                      <p className="mt-3 text-xs leading-relaxed text-slate-400">
                        Every delivery includes <span className="mono text-slate-200">X-ShipAny-Signature</span> (<span className="mono text-slate-200">sha256=…</span> HMAC-SHA256 hex of the raw body with your endpoint secret), <span className="mono text-slate-200">X-ShipAny-Event</span> and <span className="mono text-slate-200">X-ShipAny-Event-Id</span>. Failed deliveries retry up to 6 times with backoff and can be replayed from the API Console.
                      </p>
                      <div className="mono mt-4 rounded-xl bg-[#030B18] p-4 text-[11px] leading-relaxed text-slate-300">
                        import hmac, hashlib<br />
                        expected = "sha256=" + hmac.new(<br />
                        {"  "}secret.encode(), raw_body, hashlib.sha256<br />
                        ).hexdigest()<br />
                        assert hmac.compare_digest(expected, signature)
                      </div>
                    </div>
                  </div>
                </DocSection>

                <DocSection id="international" kicker="International" title="Cross-Border Shipping">
                  <p className="text-sm leading-relaxed text-slate-400">
                    International endpoints cover destination serviceability, multi-courier rates, booking with item-level customs data (description, HS code, quantity, value, country of origin), tracking, labels and customs invoices. Rates, transit times and customs requirements vary by destination and carrier.
                  </p>
                </DocSection>

                <DocSection id="wallet" kicker="Wallet" title="Balance & Ledger">
                  <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
                    <span className="mono rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400">GET</span>
                    <span className="mono ml-2 text-xs font-bold text-white">/v1/wallet/balance</span>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400">
                      Your API wallet is isolated from any merchant wallet. Bookings debit atomically at the quoted rate; cancellations before pickup refund automatically. Admin-assigned credit limits let you ship now and settle later. <span className="mono rounded bg-white/5 px-1.5 py-0.5 text-xs text-slate-200">GET /v1/wallet/transactions</span> returns the full ledger, and <span className="mono text-slate-200">wallet.debited</span> / <span className="mono text-slate-200">wallet.refunded</span> webhooks keep your systems in sync.
                    </p>
                  </div>
                </DocSection>

                <DocSection id="reference" kicker="Reference" title="Errors, Status & Changelog">
                  <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="dev-errors-table">
                    <div className="mono grid grid-cols-[72px_1fr] gap-2 bg-white/5 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      <span>Code</span><span>Meaning</span>
                    </div>
                    {ERRORS.map(([code, desc]) => (
                      <div key={code} className="grid grid-cols-[72px_1fr] gap-2 border-t border-white/5 px-4 py-3" data-testid={`dev-error-${code}`}>
                        <span className="mono text-xs font-bold text-[#FF9933]">{code}</span>
                        <span className="text-xs text-slate-400">{desc}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 grid gap-5 md:grid-cols-2">
                    <Link to="/status" data-testid="dev-status-link"
                      className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:border-shipany-cyan/40">
                      <Activity className="h-6 w-6 shrink-0 text-emerald-400" />
                      <div>
                        <h3 className="font-display text-sm font-bold text-white">API Status</h3>
                        <p className="mt-1 text-xs text-slate-400">Live component health for the API, rates, booking, tracking, webhooks and wallet.</p>
                      </div>
                    </Link>
                    <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid="dev-changelog">
                      <Repeat className="h-6 w-6 shrink-0 text-shipany-cyan" />
                      <div>
                        <h3 className="font-display text-sm font-bold text-white">Changelog</h3>
                        <p className="mono mt-1 text-xs text-slate-400"><span className="text-shipany-cyan">v1</span> — rates, serviceability, domestic + international booking, labels, pickups, manifests, NDR/RTO, returns, wallet and HMAC webhooks.</p>
                      </div>
                    </div>
                  </div>
                </DocSection>
              </div>
            </div>
          </div>
        </section>

        <GradientCta title="Start Building With ShipAny" sub="Connect your platform to a powerful shipping infrastructure."
          primary={["Open API Console", "/console/login"]} secondary={["Contact Us", "/contact"]} testId="dev-final-cta" />
      </div>
    </MarketingLayout>
  );
}
